import { AxiosResponse } from 'axios';
import axiosInstance from './axios';

export const getCompassModules = async (): Promise<any> => {
  try {
    const response: AxiosResponse = await axiosInstance.get('/compass');
    return response.data;
  } catch (error) {
    throw new Error(`Error fetching compass modules: ${error}`);
  }
};

export const getCompassUpdates = async (params?: {
  page?: number;
  limit?: number;
}): Promise<any> => {
  try {
    const response: AxiosResponse = await axiosInstance.get('/compass/updates/list', {
      params: { page: params?.page ?? 1, limit: params?.limit ?? 3 }
    });
    return response.data;
  } catch (error) {
    throw new Error(`Error fetching compass updates: ${error}`);
  }
};

export const getCompassModuleById = async (id: string): Promise<any> => {
  try {
    const response: AxiosResponse = await axiosInstance.get(`/compass/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(`Error fetching compass module by id: ${error}`);
  }
};

export const createCompassModule = async (data: any): Promise<any> => {
  try {
    const response: AxiosResponse = await axiosInstance.post(`/compass`, data);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'internal service error';
    throw new Error(`Error creating the compass module: ${errorMessage}`);
  }
};

export const updateCompassModule = async (id: string, data: any): Promise<any> => {
  try {
    const response: AxiosResponse = await axiosInstance.put(`/compass/${id}`, data);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'internal service error';
    throw new Error(`Error updating the compass module: ${errorMessage}`);
  }
};

export const deleteCompassModule = async (id: string): Promise<any> => {
  try {
    const response: AxiosResponse = await axiosInstance.delete(`/compass/${id}`);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'internal service error';
    throw new Error(`Error deleting the compass module: ${errorMessage}`);
  }
};

export const updateCompassModuleWorkflow = async (
  id: string,
  payload: {
    state: 'staging' | 'production';
    send_email?: boolean;
    updates?: {
      type: 'module_added' | 'methodology_added';
      image?: string;
      name?: string;
      field_name?: string;
      version?: string;
      standard?: string;
    };
    newMethodologies?: Array<{
      name: string;
      version: string;
      standard: string;
    }>;
  }
): Promise<any> => {
  try {
    const response: AxiosResponse = await axiosInstance.put(`/compass/${id}/workflow`, payload);
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'internal service error';
    throw new Error(`Error updating compass module workflow: ${errorMessage}`);
  }
};

export const uploadCompassUpdateImage = async (file: File): Promise<{ fileKey: string }> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response: AxiosResponse = await axiosInstance.post(`/compass/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data?.url || response.data?.location || response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'internal service error';
    throw new Error(`Error uploading image: ${errorMessage}`);
  }
};

export type CompassPublishPreview = {
  isModuleLive: boolean;
  diffMethodologies: Array<{
    name: string;
    version: string;
    standard: string;
    preview_text: string;
    type: 'methodology_added' | 'module_added';
  }>;
};

export const getCompassPublishPreview = async (id: string): Promise<CompassPublishPreview> => {
  try {
    const response: AxiosResponse = await axiosInstance.get(`/compass/${id}/publish-preview`);
    return response.data as CompassPublishPreview;
  } catch (error: any) {
    throw new Error(`Error fetching compass publish preview: ${error}`);
  }
};
